// The bot check, attached to every anonymous call that CHANGES something.
//
// WHY AN INTERCEPTOR AND NOT TEN CALL SITES. A reCAPTCHA v3 token is minted
// per request (it expires after two minutes and is spent on first
// verification), so it cannot be threaded through `siteAuthHeaders`, which is
// synchronous. Ten `await mintCaptcha()` calls sprinkled through the site-txn
// client would be ten places to forget one. One request interceptor is the
// single point where a protected write acquires its token — the same shape as
// the embed token riding `X-Embed-Token` beside it.
//
// WHY ONLY WRITES. A list, a model fetch or a rights check happens many times
// per page and would cost a Google round-trip each, for a call that changes
// nothing. A script scraping a public list is a rate-limit problem; a script
// filling the application's tables is this one.

import type { InternalAxiosRequestConfig } from 'axios'
import api from './client'
import { CAPTCHA_HEADER, CAPTCHA_ACTIONS, executeRecaptcha } from '@twasta/page-runtime/captcha'
import { getSiteToken } from './website'

export interface PublicCaptchaConfig {
  enabled: boolean
  provider: string
  site_key: string
  surfaces: string[]
}

const EMPTY: PublicCaptchaConfig = {
  enabled: false, provider: '', site_key: '', surfaces: [],
}

// One in-flight fetch per project, and the answer kept for the session. The
// site key is a published constant, so re-asking on every submit would be a
// round-trip that can only ever return the same string.
const configs = new Map<string, Promise<PublicCaptchaConfig>>()

function key(userId: string, project: string) { return `${userId}/${project}` }

/** The project's published site key. Resolves to a disabled config on any
 *  failure — a project with no check and a backend that could not answer look
 *  the same to a caller, and both mean "send the request without a token". */
export function fetchCaptchaConfig(
  userId: string, project: string,
): Promise<PublicCaptchaConfig> {
  const k = key(userId, project)
  const hit = configs.get(k)
  if (hit) return hit
  const promise = api
    .get<PublicCaptchaConfig>(
      `/website/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/captcha-config`)
    .then((r) => r.data || EMPTY)
    .catch(() => EMPTY)
  configs.set(k, promise)
  return promise
}

/** Drop the cached answer — for the Spam protection screen, which changes it. */
export function invalidateCaptchaConfig(userId?: string, project?: string) {
  if (userId && project) configs.delete(key(userId, project))
  else configs.clear()
}

/** Mint a token for one surface of one project, or null when no check
 *  applies. Always resolves: a blocked script must cost the protection, never
 *  the request. */
export async function mintCaptchaToken(
  userId: string, project: string, surface: keyof typeof CAPTCHA_ACTIONS,
): Promise<string | null> {
  const cfg = await fetchCaptchaConfig(userId, project)
  if (!cfg.enabled || !cfg.site_key) return null
  if (cfg.surfaces?.length && !cfg.surfaces.includes(surface)) return null
  return executeRecaptcha(cfg.site_key, CAPTCHA_ACTIONS[surface])
}

// ── Which requests are protected ────────────────────────────────────────────

// `/website/{owner}/{project}/site-txn/...` and `/site-doc/upload`.
const SITE_PATH = /\/website\/([^/]+)\/([^/]+)\/(site-txn|site-doc)(\/.*)?$/

// The `execute` actions that change something. Mirrors `WRITE_CAPABILITIES` in
// `routers/website.py`: the server refuses without a token, so a new write
// action missing from here is a write the user is told they may not make.
const WRITE_ACTIONS = new Set([
  'add', 'edit', 'save', 'delete', 'run_action', 'run_action_bulk',
])

function protectedSurface(config: InternalAxiosRequestConfig):
  { userId: string; project: string; surface: 'transactions' | 'uploads' } | null {
  const url = config.url || ''
  const match = SITE_PATH.exec(url.split('?')[0])
  if (!match) return null
  const [, userId, project, family, rest = ''] = match

  if (family === 'site-doc') {
    return rest === '/upload'
      ? { userId: decodeURIComponent(userId), project: decodeURIComponent(project),
          surface: 'uploads' }
      : null
  }

  const txn = { userId: decodeURIComponent(userId), project: decodeURIComponent(project),
                surface: 'transactions' as const }
  if (rest.endsWith('/execute-batch') || rest === '/process') return txn
  if (rest.endsWith('/execute')) {
    // The body names the action, and only some of them write.
    const body = config.data
    const action = typeof body === 'string'
      ? (() => { try { return JSON.parse(body)?.action } catch { return undefined } })()
      : (body as { action?: string } | undefined)?.action
    return action && WRITE_ACTIONS.has(action) ? txn : null
  }
  return null
}

let installed = false

/**
 * Attach `X-Twasta-Captcha` to anonymous site writes. Idempotent — the app
 * calls this once at boot.
 *
 * A SIGNED-IN site visitor is skipped: the check guards the anonymous door,
 * and making an authenticated user's every save wait on a Google round-trip
 * would be a cost with no matching risk (the server skips it for them too).
 */
export function installCaptchaInterceptor() {
  if (installed) return
  installed = true
  api.interceptors.request.use(async (config) => {
    try {
      const target = protectedSurface(config)
      if (!target) return config
      if (getSiteToken(target.userId, target.project)) return config
      const token = await mintCaptchaToken(target.userId, target.project, target.surface)
      if (token) {
        config.headers = config.headers || {}
        ;(config.headers as Record<string, string>)[CAPTCHA_HEADER] = token
      }
    } catch {
      // A failure to mint must never stop the request from being sent: the
      // server decides what a missing token means, and here that is a 403 the
      // user can act on, not a request that silently never left.
    }
    return config
  })
}
